import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { Injectable, InjectionToken, Injector } from "@angular/core";
import DefaultConfig from "../config/config.json";
import { Constants, CUSTOM_SORT, EvaluationFlowsEnum, LogLine, Node } from "../models/shared.model";
import { EvaluatorResult } from "../models/evaluator-result";
import { WaitService } from "./wait.service";
import { TranslateService } from "@ngx-translate/core";
import type { MessageService } from "./message.service";
import type { LinksService } from "./links.service";

// Resolve export-only sources lazily: MessageService itself needs LogService.
export const LOG_MESSAGE_SOURCE = new InjectionToken<MessageService>('Log message source');
export const LOG_LINKS_SOURCE = new InjectionToken<LinksService>('Log links source');

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private appInsights: ApplicationInsights;
  public logsArr: LogLine[] = [];

  constructor(
    private waitService: WaitService,
    private translateService: TranslateService,
    private injector: Injector
  ) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: DefaultConfig.AppInsightsKey,
        enableUnhandledPromiseRejectionTracking: true
      }
    });

    this.waitService.resetData.subscribe((reset) => {
      if (reset) {
        this.logsArr = [];
      }
    });
  }

  private get messageService(): MessageService { return this.injector.get(LOG_MESSAGE_SOURCE); }
  private get linksService(): LinksService { return this.injector.get(LOG_LINKS_SOURCE); }

  getLogs() {
    return this.logsArr;
  }

  addLog(logLine: LogLine) {
    this.logsArr.push(logLine);
    this.sortLogs();
  }

  appendEvaluatorLogs(evaluatorLogs: any) {
    this.logsArr = [...this.logsArr, ...evaluatorLogs];
  }

  updateLog(logId: number, reasonLog: string, decision: string, points: number, node?: Node, flowType?: string) {
    let rowIndex = this.logsArr.findIndex(row => row.id === logId);
    if (rowIndex != -1) {
      let updatedRow = { ...this.logsArr[rowIndex] };
      updatedRow.result = decision;
      updatedRow.reason = reasonLog;
      updatedRow.points = points;
      this.logsArr[rowIndex] = updatedRow;
    } else {
      this.logsArr.push(new LogLine(logId, flowType, node.id, decision, reasonLog, points));
    }

    this.sortLogs();
  }

  addChosenItemLog(analysisResult: EvaluatorResult[]) {
    let chosenLink = analysisResult
      .filter(ele => ele.name == EvaluationFlowsEnum.LINKS)
      .reduce((prev, current) =>
        (prev?.weight > current?.weight) ? prev : current, null);

    let chosenAttachment = analysisResult
      .filter(ele => ele.name == EvaluationFlowsEnum.ATTACHMENTS)
      .reduce((prev, current) =>
        (prev?.weight > current?.weight) ? prev : current, null);

    let chosenQrCode = analysisResult
      .filter(ele => ele.name == EvaluationFlowsEnum.QRCODE)
      .reduce((prev, current) =>
        (prev?.weight > current?.weight) ? prev : current, null);

    if (chosenLink) {
      this.addLog(new LogLine(Date.now() + Math.random(), chosenLink.name, "Chosen link in the score",
        chosenLink.additionalInfo.link, "This link has max score", chosenLink.weight, null, chosenLink.additionalInfo.displayedAs));
    }
    if (chosenAttachment) {
      this.addLog(new LogLine(Date.now() + Math.random(), chosenAttachment.name, "Chosen attachment in the score",
        chosenAttachment.additionalInfo.attachment.name, "This attachment has max score", chosenAttachment.weight));
    }
    if (chosenQrCode) {
      this.addLog(new LogLine(Date.now() + Math.random(), chosenQrCode.name, "Chosen qr code in the score",
        chosenQrCode.additionalInfo.qrCode.decoded, "This qr code has max score", chosenQrCode.weight));
    }
  }

  multipleConditionsLog(groupTitle: string, points: number, logMsg: string) {
    let groupLogExists = this.logsArr.find(value => value.check == groupTitle);

    if (!groupLogExists)
      this.logsArr.push(new LogLine(-1, Constants.CONDITIONS_GROUP, groupTitle, "true", logMsg, points));

    this.sortLogs();
  }

  sortLogs() {
    // Create a mapping of names to their order index
    const orderMap: { [key: string]: number } = {};
    CUSTOM_SORT.forEach((name, index) => {
      orderMap[name] = index;
    });

    this.logsArr = this.logsArr.sort((a, b) => {
      const orderA = orderMap[a.flow] !== undefined ? orderMap[a.flow] : Infinity; // Use Infinity for undefined values
      const orderB = orderMap[b.flow] !== undefined ? orderMap[b.flow] : Infinity;
      return orderA - orderB; // Sort based on custom order
    });
  }


  public logException(error: Error): void {
    this.appInsights.trackException({ exception: error });
  }

  public logMessage(message: string): void {
    this.appInsights.trackTrace({ message });
  }

  public logPageView(page: string): void {
    this.appInsights.trackPageView({ name: page });
  }

  public setUserId(userId: string): void {
    this.appInsights.setAuthenticatedUserContext(userId, undefined, true);
  }

  public generateLogFileAndName(includeBodyAndLinks: boolean = false): { data: string; fileName: string } {

    let logData = `${this.translateService.instant('logs.authentication_mechanism')}:
    ${this.translateService.instant('logs.sso_naa')} \n`;

    this.getLogs().forEach(el => {
      let linkLog = "", attachmentLog = "", qrCodeLog = "";
      if (el.linkDisplayedAs)
        linkLog += `, ${this.translateService.instant('logs.link_displayed_as')}: ${el.linkDisplayedAs}`;
      if (el.link)
        linkLog += `, ${this.translateService.instant('logs.link')}: ${el.link.getAttribute('href')}`;
      if (el.attachment)
        attachmentLog = `, ${this.translateService.instant('logs.attachment')}: ${el.attachment.name}`;
      if (el.qrCode)
        qrCodeLog = `, ${this.translateService.instant('logs.qr_code')}: ${el.qrCode.decoded}`;

      logData += `${this.translateService.instant('logs.flow')}: ${el.flow},
      ${this.translateService.instant('logs.check')}: ${el.check},
      ${this.translateService.instant('logs.result')}: ${el.result}, 
      ${this.translateService.instant('logs.reason')}: ${el.reason}, 
      ${this.translateService.instant('logs.points_added')}: ${el.points} ${linkLog} ${attachmentLog} ${qrCodeLog}\n`;
    });

    let data = logData
      + '\n\n-----------------------------------------------'
      + `\n${this.translateService.instant('logs.headers')}\n`
      + '-----------------------------------------------\n'
      + this.messageService.getHeadersAsString();

    if(includeBodyAndLinks) {
      data += "\n\n-----------------------------------------------"
        + `\n${this.translateService.instant("logs.email_body_raw")}\n`
        + "-----------------------------------------------\n"
        + this.linksService.rawEmailBody;

      data += "\n\n-----------------------------------------------"
        + `\n${this.translateService.instant("logs.email_body_cleaned")}\n`
        + "-----------------------------------------------\n"
        + this.linksService.emailBody;

      data += "\n\n-----------------------------------------------"
        + `\n${this.translateService.instant("logs.email_links")}\n`
        + "-----------------------------------------------\n";

      this.linksService.linksWStyles.forEach((item, index) => {
        data += `\n ${index}) ${item.link.toString()}\n`;
      });
    }

    const email = Office.context.mailbox.userProfile.emailAddress;
    const timestamp = new Date().getTime();
    const fileName = `${email}-${timestamp}.txt`;

    return { data, fileName };
  }
}
